import { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "../api";
import Layout from "../components/Layout";
import { useNavigate } from "react-router-dom";

function formatDate(value) {
  if (!value) return "-";
  return new Intl.DateTimeFormat("pt-BR").format(new Date(`${value}T00:00:00`));
}

function getWeekBounds() {
  const now = new Date();
  const day = now.getDay();
  const diffToMonday = day === 0 ? -6 : 1 - day;
  const monday = new Date(now);
  monday.setDate(now.getDate() + diffToMonday);
  const sunday = new Date(monday);
  sunday.setDate(monday.getDate() + 6);

  const toInput = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const dayOfMonth = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${dayOfMonth}`;
  };

  return {
    startDate: toInput(monday),
    endDate: toInput(sunday),
  };
}

function StudentNames({ items }) {
  if (!items?.length) {
    return <span className="text-muted">Nenhum aluno</span>;
  }

  return items.map((item) => item.name).join(", ");
}

function escapeHtml(value) {
  return String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function studentListHtml(items) {
  if (!items?.length) {
    return '<p class="muted">Nenhum aluno</p>';
  }

  return `<ul>${items
    .map((item) => `<li>${escapeHtml(item.name)}</li>`)
    .join("")}</ul>`;
}

function buildConclusion(report) {
  if (report.studentsWithAbsences === 0) {
    return "A turma nao apresentou faltas no periodo selecionado.";
  }

  if (report.absenceBuckets.threeOrMore.count > 0) {
    return "A turma apresenta concentracao de faltas em um grupo especifico de alunos, exigindo acompanhamento mais proximo da coordenacao.";
  }

  if (report.absenceBuckets.twoAbsences.count > 0) {
    return "A turma apresenta baixa incidencia geral de faltas, com alguns casos que merecem monitoramento semanal.";
  }

  return "A turma apresenta faltas pontuais e dispersas, sem concentracao relevante no periodo analisado.";
}

export default function AttendanceReportPage() {
  const navigate = useNavigate();
  const defaultRange = useMemo(() => getWeekBounds(), []);

  const [classes, setClasses] = useState([]);
  const [reports, setReports] = useState([]);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    classId: "",
    startDate: defaultRange.startDate,
    endDate: defaultRange.endDate,
  });

  const fetchClasses = useCallback(async () => {
    const response = await api.get("/classes");
    const list = Array.isArray(response.data) ? response.data : response.data?.data || [];
    setClasses(list);
  }, []);

  const fetchReport = useCallback(async (nextFilters = filters) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set("startDate", nextFilters.startDate);
      params.set("endDate", nextFilters.endDate);

      if (nextFilters.classId) {
        params.set("classId", String(nextFilters.classId));
      }

      const response = await api.get(`/dashboard/attendance-report?${params.toString()}`);
      setReports(response.data?.classes || []);
      setPage(1);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchClasses();
  }, [fetchClasses]);

  useEffect(() => {
    fetchReport({
      classId: "",
      startDate: defaultRange.startDate,
      endDate: defaultRange.endDate,
    });
  }, [defaultRange.endDate, defaultRange.startDate, fetchReport]);

  function applyFilters() {
    fetchReport(filters);
  }

  function clearFilters() {
    const nextFilters = {
      classId: "",
      startDate: defaultRange.startDate,
      endDate: defaultRange.endDate,
    };
    setFilters(nextFilters);
    fetchReport(nextFilters);
  }

  function exportToWord() {
    if (reports.length === 0) {
      return;
    }

    const sections = reports.map((report) => `
      <section class="report-section">
        <h2>Turma: ${escapeHtml(report.className)}</h2>
        <p class="subtitle">Relatorio Resumido da Turma - Acompanhamento de Frequência (${escapeHtml(formatDate(filters.startDate))} a ${escapeHtml(formatDate(filters.endDate))})</p>

        <p><strong>Total de alunos na turma:</strong> ${report.totalStudents}</p>
        <p><strong>Alunos sem faltas no periodo:</strong> ${report.studentsWithoutAbsences}</p>
        <p><strong>Alunos com pelo menos uma falta:</strong> ${report.studentsWithAbsences}</p>

        <h3>Situacao de Frequencia</h3>
        <p><strong>3 ou mais faltas na semana:</strong> ${report.absenceBuckets.threeOrMore.count} aluno(s)</p>
        ${studentListHtml(report.absenceBuckets.threeOrMore.students)}

        <p><strong>2 faltas:</strong> ${report.absenceBuckets.twoAbsences.count} aluno(s)</p>
        ${studentListHtml(report.absenceBuckets.twoAbsences.students)}

        <p><strong>1 falta:</strong> ${report.absenceBuckets.oneAbsence.count} aluno(s)</p>
        ${studentListHtml(report.absenceBuckets.oneAbsence.students)}

        <h3>Casos Resolvidos</h3>
        ${studentListHtml(report.cases.resolved.students)}

        <h3>Casos em Acompanhamento</h3>
        <p><strong>Entrar em contato com escola ou responsavel:</strong></p>
        ${studentListHtml(report.cases.monitoringContact.students)}

        <p><strong>Sem confirmacao de motivo / em monitoramento:</strong></p>
        ${studentListHtml(report.cases.monitoringOnly.students)}

        <h3>Conclusao</h3>
        <p>${escapeHtml(buildConclusion(report))}</p>
        <p><strong>Resumo sugerido:</strong> ${escapeHtml(report.draftSummary)}</p>
      </section>
    `).join('<div class="page-break"></div>');

    const html = `
      <html>
        <head>
          <meta charset="utf-8" />
          <title>Relatorio de Frequência</title>
          <style>
            body {
              font-family: Calibri, Arial, sans-serif;
              color: #1f2937;
              margin: 32px;
              line-height: 1.45;
              font-size: 12pt;
            }
            h1 {
              font-size: 22pt;
              margin: 0 0 8px;
              color: #0f4c9a;
            }
            h2 {
              font-size: 16pt;
              margin: 0 0 8px;
              color: #0b2b5b;
            }
            h3 {
              font-size: 13pt;
              margin: 18px 0 8px;
              color: #0f172a;
            }
            p {
              margin: 6px 0;
            }
            ul {
              margin: 6px 0 12px 20px;
              padding: 0;
            }
            li {
              margin: 4px 0;
            }
            .subtitle {
              color: #475569;
              margin-bottom: 14px;
            }
            .report-section {
              padding: 18px 22px;
              border: 1px solid #cbd5e1;
              border-radius: 12px;
              background: #ffffff;
            }
            .page-break {
              page-break-before: always;
              margin-top: 24px;
            }
            .muted {
              color: #64748b;
            }
            .cover {
              border-bottom: 2px solid #cbd5e1;
              padding-bottom: 16px;
              margin-bottom: 24px;
            }
          </style>
        </head>
        <body>
          <div class="cover">
            <h1>Relatorio Resumido das Turmas</h1>
            <p><strong>Acompanhamento de Frequencia</strong></p>
            <p><strong>Periodo:</strong> ${escapeHtml(formatDate(filters.startDate))} a ${escapeHtml(formatDate(filters.endDate))}</p>
            <p><strong>Total de turmas no arquivo:</strong> ${reports.length}</p>
          </div>
          ${sections}
        </body>
      </html>
    `;

    const blob = new Blob([html], { type: "text/html;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `relatorio-frequencia-${filters.startDate}-a-${filters.endDate}.html`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }

  const pageSize = 5;
  const totalPages = Math.max(1, Math.ceil(reports.length / pageSize));
  const paginatedReports = reports.slice((page - 1) * pageSize, page * pageSize);

  return (
    <Layout>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h4 className="mb-1">Relatório Semanal de Frequência</h4>
          <small className="text-muted">Resumo por turma para acompanhamento da coordenação</small>
        </div>
        <div className="d-flex gap-2">
          <button
            className="btn btn-outline-primary"
            onClick={exportToWord}
            disabled={loading || reports.length === 0}
          >
            Exportar HTML para Word
          </button>
          <button className="btn btn-outline-secondary" onClick={() => navigate("/analytics")}>
            Voltar ao Analytics
          </button>
        </div>
      </div>

      <div className="card shadow-sm p-3 mb-3">
        <div className="row g-2 align-items-end">
          <div className="col-md-3">
            <label className="form-label">Turma</label>
            <select
              className="form-select"
              value={filters.classId}
              onChange={(e) => setFilters((prev) => ({ ...prev, classId: e.target.value }))}
            >
              <option value="">Todas as turmas</option>
              {classes.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.name}
                </option>
              ))}
            </select>
          </div>

          <div className="col-md-3">
            <label className="form-label">Data inicial</label>
            <input
              type="date"
              className="form-control"
              value={filters.startDate}
              onChange={(e) => setFilters((prev) => ({ ...prev, startDate: e.target.value }))}
            />
          </div>

          <div className="col-md-3">
            <label className="form-label">Data final</label>
            <input
              type="date"
              className="form-control"
              value={filters.endDate}
              onChange={(e) => setFilters((prev) => ({ ...prev, endDate: e.target.value }))}
            />
          </div>

          <div className="col-md-3 d-flex gap-2">
            <button className="btn btn-primary" onClick={applyFilters}>
              Gerar Relatório
            </button>
            <button className="btn btn-outline-secondary" onClick={clearFilters}>
              Limpar
            </button>
          </div>
        </div>
      </div>

      <div className="mb-3 text-muted">
        Periodo selecionado: {formatDate(filters.startDate)} a {formatDate(filters.endDate)}
      </div>

      {loading ? (
        <div className="card shadow-sm p-4">Carregando Relatório...</div>
      ) : reports.length === 0 ? (
        <div className="card shadow-sm p-4">Nenhuma turma encontrada para o filtro selecionado.</div>
      ) : (
        <>
          <div className="report-grid">
            {paginatedReports.map((report) => (
              <div className="card shadow-sm p-4" key={report.classId}>
                <div className="d-flex justify-content-between align-items-start mb-3 gap-3">
                  <div>
                    <h5 className="mb-1">Turma: {report.className}</h5>
                    <div className="text-muted">{report.draftSummary}</div>
                  </div>
                  <span className="badge text-bg-light border">{report.totalStudents} alunos</span>
                </div>

                <div className="row g-3 mb-3">
                  <div className="col-md-4">
                    <div className="report-stat-card">
                      <div className="report-stat-label">Sem faltas no periodo</div>
                      <div className="report-stat-value">{report.studentsWithoutAbsences}</div>
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="report-stat-card">
                      <div className="report-stat-label">Com pelo menos uma falta</div>
                      <div className="report-stat-value">{report.studentsWithAbsences}</div>
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="report-stat-card">
                      <div className="report-stat-label">Casos resolvidos</div>
                      <div className="report-stat-value">{report.cases.resolved.count}</div>
                    </div>
                  </div>
                </div>

                <div className="table-responsive mb-3">
                  <table className="table table-sm align-middle">
                    <thead>
                      <tr>
                        <th>Indicador</th>
                        <th>Total</th>
                        <th>Alunos</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td>3 ou mais faltas na semana</td>
                        <td>{report.absenceBuckets.threeOrMore.count}</td>
                        <td><StudentNames items={report.absenceBuckets.threeOrMore.students} /></td>
                      </tr>
                      <tr>
                        <td>2 faltas na semana</td>
                        <td>{report.absenceBuckets.twoAbsences.count}</td>
                        <td><StudentNames items={report.absenceBuckets.twoAbsences.students} /></td>
                      </tr>
                      <tr>
                        <td>1 falta na semana</td>
                        <td>{report.absenceBuckets.oneAbsence.count}</td>
                        <td><StudentNames items={report.absenceBuckets.oneAbsence.students} /></td>
                      </tr>
                      <tr>
                        <td>Em acompanhamento com contato pendente</td>
                        <td>{report.cases.monitoringContact.count}</td>
                        <td><StudentNames items={report.cases.monitoringContact.students} /></td>
                      </tr>
                      <tr>
                        <td>Em monitoramento inicial</td>
                        <td>{report.cases.monitoringOnly.count}</td>
                        <td><StudentNames items={report.cases.monitoringOnly.students} /></td>
                      </tr>
                      <tr>
                        <td>Casos resolvidos</td>
                        <td>{report.cases.resolved.count}</td>
                        <td><StudentNames items={report.cases.resolved.students} /></td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            ))}
          </div>

          <div className="d-flex justify-content-center align-items-center mt-3">
            <button
              className="btn btn-outline-secondary me-3"
              disabled={page <= 1}
              onClick={() => setPage((current) => current - 1)}
            >
              Anterior
            </button>

            <span>
              Pagina {page} de {totalPages}
            </span>

            <button
              className="btn btn-outline-secondary ms-3"
              disabled={page >= totalPages}
              onClick={() => setPage((current) => current + 1)}
            >
              Proxima
            </button>
          </div>
        </>
      )}
    </Layout>
  );
}

